import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { UserPlus, Link, Gamepad2, Calendar, Award, ExternalLink, Sparkles, DollarSign, Gem } from 'lucide-react'
import useAppStore from '../store/useAppStore'
import useAppEnvironment from '../hooks/useAppEnvironment'
import { userApi } from '../services'
import toast from 'react-hot-toast'
import HelpModal from './HelpModal'
import { closePreparedBrowserLinkWindow, openTelegramOrBrowserLink, prepareBrowserLinkWindow } from '../utils/openTelegramOrBrowserLink'

const TasksList = () => {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const { tasks, user, shareInviteMessage } = useAppStore()
  const { isWebBrowser } = useAppEnvironment()
  const [instructionModal, setInstructionModal] = useState({
    isOpen: false,
    title: '',
    htmlContent: ''
  })

  const browserHiddenTaskTypes = [
    'addBioTg',
    'changeAvaTg',
    'subscribeChannel',
    'referralLinkBioTg',
    'joinChat',
    'addBioSteam',
    'changeAvaSteam'
  ]

  // Маппинг иконок для разных типов заданий
  const getTaskIcon = (taskName) => {
    const name = taskName.toLowerCase()
    if (name.includes('друг') || name.includes('friend')) return UserPlus
    if (name.includes('steam')) return Link
    if (name.includes('faceit')) return Gamepad2
    if (name.includes('бонус') || name.includes('bonus')) return Calendar
    return Award
  }

  const openInstructionModal = (task, htmlContent) => {
    if (!htmlContent) return

    setInstructionModal({
      isOpen: true,
      title: getTaskName(task),
      htmlContent
    })
  }

  const closeInstructionModal = () => {
    setInstructionModal({
      isOpen: false,
      title: '',
      htmlContent: ''
    })
  }

  // Обработка клика по заданию
  const handleTaskClick = async (task) => {
    if (!task.active) return

    try {
      // Специальная обработка для задания "Пригласить друга"
      if (task.type === 'inviteFriend') {
        // Переходим на страницу рефералов
        navigate('/referrals')
        return
      }

      // Специальная обработка для задания пополнения
      if (task.type === 'topUp') {
        navigate('/payment')
        return
      }

      // Специальная обработка для подписки на канал
      if (task.type === 'subscribeChannel') {
        const preparedWindow = prepareBrowserLinkWindow()

        try {
          // Отправляем запрос на бекенд
          await userApi.completeTask(task.id)
          
          // Открываем канал из данных пользователя
          const channelUrl = user?.tg_channel
          if (channelUrl) {
            openTelegramOrBrowserLink(channelUrl, { preparedWindow })
          } else {
            closePreparedBrowserLinkWindow(preparedWindow)
            toast.error(t('tasks.channelUnavailable'))
          }
        } catch (error) {
          closePreparedBrowserLinkWindow(preparedWindow)
          console.error('Ошибка при обработке подписки на канал:', error)
          // Не показываем toast с ошибкой, просто логируем
        }
        return
      }

      // Специальная обработка для вступления в чат
      if (task.type === 'joinChat') {
        const preparedWindow = prepareBrowserLinkWindow()

        try {
          // Отправляем запрос на бекенд
          await userApi.completeTask(task.id)
          
          // Открываем чат из данных пользователя
          const chatUrl = user?.tg_chat
          if (chatUrl) {
            openTelegramOrBrowserLink(chatUrl, { preparedWindow })
          } else {
            closePreparedBrowserLinkWindow(preparedWindow)
            toast.error(t('tasks.chatUnavailable'))
          }
        } catch (error) {
          closePreparedBrowserLinkWindow(preparedWindow)
          console.error('Ошибка при обработке вступления в чат:', error)
          // Не показываем toast с ошибкой, просто логируем
        }
        return
      }

      // Типы заданий, которые требуют отправки инструкции в Telegram
      const instructionTaskTypes = [
        'addBioTg',
        'changeAvaTg', 
        'addBioSteam',
        'changeAvaSteam',
        'referralLinkBioTg'
      ]

      // Обработка других типов заданий
      if (task.action && task.action.startsWith('http')) {
        // Открываем внешнюю ссылку
        openTelegramOrBrowserLink(task.action)
      } else {
        // Выполняем задание через API
        const response = await userApi.completeTask(task.id)
        
        // Показываем соответствующее уведомление
        if (instructionTaskTypes.includes(task.type)) {
          if (response?.html) {
            openInstructionModal(task, response.html)
          } else {
            toast.success(t('tasks.instructionSent'))
          }
        } else {
          toast.success(t('tasks.taskCompleted', { bonus: task.bonus }))
        }
      }
    } catch (error) {
      console.error('Ошибка выполнения задания:', error)
      
      // Показываем соответствующее сообщение об ошибке
      if (error.message === 'Telegram WebApp API недоступен') {
        toast.error(t('common.telegramNotAvailable'))
      } else {
        toast.error(t('tasks.taskError'))
      }
    }
  }

  // Получаем название задания (теперь приходит готовая строка с бекенда)
  const getTaskName = (task) => {
    return task.name || t('tasks.defaultTaskName')
  }

  // Получаем локализованную частоту награды
  const getRewardFrequency = (frequency) => {
    if (!frequency) return ''
    return t(`tasks.frequency.${frequency}`) || ''
  }

  // Группировка заданий по категориям
  const groupedTasks = useMemo(() => {
    if (!tasks || tasks.length === 0) return {}

    const groups = {
      top: [],
      subscriptions: [],
      daily: [],
      other: []
    }

    // Фильтруем только активные задания и распределяем по категориям
    tasks.forEach(task => {
      if (!task.active) return // Пропускаем неактивные задания
      if (isWebBrowser && browserHiddenTaskTypes.includes(task.type)) return

      const taskType = task.type

      // ТОП задания (самые профитные)
      if (taskType === 'inviteFriend' || taskType === 'topUp') {
        groups.top.push(task)
      }
      // Кристаллы за подписки
      else if (taskType === 'subscribeChannel' || taskType === 'joinChat') {
        groups.subscriptions.push(task)
      }
      // Ежедневная халява
      else if (
        taskType === 'dailyBonus' ||
        taskType === 'addBioTg' ||
        taskType === 'changeAvaTg' ||
        taskType === 'addBioSteam' ||
        taskType === 'changeAvaSteam'
      ) {
        groups.daily.push(task)
      }
      // Прочее
      else {
        groups.other.push(task)
      }
    })

    // Удаляем пустые категории
    Object.keys(groups).forEach(key => {
      if (groups[key].length === 0) {
        delete groups[key]
      }
    })

    return groups
  }, [tasks, isWebBrowser])

  // Рендер карточки задания
  const renderTaskCard = (task, isTopTask = false) => {
    const Icon = getTaskIcon(getTaskName(task))
    const hasExternalAction = task.action && task.action.startsWith('http')
    
    return (
      <div 
        key={task.id}
        onClick={() => handleTaskClick(task)}
        className="bg-surface rounded-lg p-4 border border-white border-opacity-10 shadow-[inset_0_1px_0_rgba(255,255,255,0.05)] hover:border-accent-primary/30 hover:shadow-[inset_0_1px_0_rgba(255,255,255,0.05),0_0_20px_rgba(0,255,127,0.12)] active:scale-[0.98] cursor-pointer transition-all"
      >
        <div className="flex items-center gap-4">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
            isTopTask
              ? 'bg-gradient-to-br from-yellow-400 to-orange-500'
              : 'bg-gradient-to-br from-accent-primary to-accent-secondary'
          }`}>
            <Icon size={20} className="text-dark-primary" />
          </div>
          
          <div className="flex-1">
            <div className="font-semibold text-white">{getTaskName(task)}</div>
            <div className="flex items-center gap-2 flex-wrap">
              <div className="flex items-center gap-1 text-sm text-accent-primary">
                <span className="font-semibold">+{task.bonus}</span>
                <Gem size={14} className="text-accent-primary" />
              </div>
              {task.reward_frequency && (
                <span className="text-sm text-text-secondary">{getRewardFrequency(task.reward_frequency)}</span>
              )}
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            {hasExternalAction && (
              <ExternalLink size={16} className="text-text-secondary" />
            )}
          </div>
        </div>
      </div>
    )
  }

  return (
    <>
      <div className="space-y-6 py-4 lg:py-0">
        {Object.keys(groupedTasks).length > 0 ? (
          <div className="space-y-6">
            {Object.entries(groupedTasks).map(([category, categoryTasks]) => (
              <div key={category} className="space-y-3">
                {/* Заголовок категории */}
                <h1 className="text-xl font-semibold text-white">
                  {t(`tasks.categories.${category}`)}
                </h1>
                
                {/* Задания в категории */}
                <div className="space-y-3">
                  {categoryTasks.map(task => renderTaskCard(task, category === 'top'))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <Calendar size={48} className="text-text-secondary mx-auto mb-4" />
            <p className="text-text-secondary">{t('tasks.loading')}</p>
          </div>
        )}
      </div>

      <HelpModal
        isOpen={instructionModal.isOpen}
        onClose={closeInstructionModal}
        title={instructionModal.title}
        htmlContent={instructionModal.htmlContent}
        highlightLastStep={false}
      />
    </>
  )
}

export default TasksList
